import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { hideDialog } from '../../../base/dialog/actions';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import Button from '../../../base/ui/components/web/Button';
import Dialog from '../../../base/ui/components/web/Dialog';
import { BUTTON_TYPES } from '../../../base/ui/constants.web';
import { CANCELLING_SCHEDULED_STATUS, CANCEL_SCHEDULED_BUTTON, SCHEDULE_BUTTON, SCHEDULE_LABEL, SCHEDULE_MAX_LEAD_MS, SCHEDULE_MIN_LEAD_MS, SCHEDULING_STATUS, cancelScheduledRequest, clearScheduledRequestProblem, defaultScheduleTime, getScheduledRequestView, noteScheduleSheetOpen, scheduleInterpreterRequestAt, scheduleRowLabel, subscribeScheduledRequest } from '../../../interpreter-queue/scheduledRequest';
const DONE_BUTTON = 'Done';
/**
 * A time as a datetime-local input's value (local time, to the minute).
 *
 * @param {Date} when - The time.
 * @returns {string}
 */
export function toLocalInputValue(when) {
    return new Date(when.getTime() - (when.getTimezoneOffset() * 60000)).toISOString()
        .slice(0, 16);
}
const useStyles = makeStyles()(theme => {
    return {
        body: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3)
        },
        // Its buttons are in the body; the dialog's own footer stays empty
        // and would leave a blank band under them.
        dialog: {
            '& > div:last-child:empty': {
                display: 'none'
            }
        },
        time: {
            ...withPixelLineHeight(theme.typography.bodyShortRegular),
            background: theme.palette.ui03,
            border: 0,
            borderRadius: theme.shape.borderRadius,
            boxSizing: 'border-box',
            color: theme.palette.text01,
            colorScheme: 'dark',
            height: 48,
            padding: '10px 16px',
            width: '100%',
            '&:focus': {
                boxShadow: `0px 0px 0px 2px ${theme.palette.focus01}`,
                outline: 0
            }
        },
        // A live region that takes room only when it has something to say.
        status: {
            ...withPixelLineHeight(theme.typography.bodyShortRegular),
            color: theme.palette.text01,
            margin: 0,
            paddingBottom: theme.spacing(4),
            textAlign: 'center',
            '&:not(:empty)': {
                paddingTop: theme.spacing(3)
            }
        }
    };
});
/**
 * The web room's "Schedule an interpreter" dialog (plan #75, as native's
 * sheet): a time 30 minutes ahead to start with (2 minutes to 30 days) and
 * Schedule. Once the server has it the dialog reads "Scheduled 3:30 PM" with
 * Cancel request and Done; opened again from More, that is its confirmation.
 * What went wrong is said here, in a few words (scheduledRequest.ts).
 *
 * @returns {JSX.Element}
 */
export default function ScheduleInterpreterDialog() {
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const request = useSyncExternalStore(subscribeScheduledRequest, getScheduledRequestView, getScheduledRequestView);
    const [value, setValue] = useState(() => toLocalInputValue(defaultScheduleTime()));
    const close = useCallback(() => dispatch(hideDialog(ScheduleInterpreterDialog)), [dispatch]);
    useEffect(() => {
        noteScheduleSheetOpen(true);
        return () => {
            noteScheduleSheetOpen(false);
            clearScheduledRequestProblem();
        };
    }, []);
    const onChange = useCallback((event) => setValue(event.target.value), []);
    const schedule = useCallback(() => scheduleInterpreterRequestAt(new Date(value)), [value]);
    const now = Date.now();
    const settled = request.phase === 'scheduled' || request.phase === 'cancelling';
    return (React.createElement(Dialog, { cancel: { hidden: true }, className: classes.dialog, ok: { hidden: true }, size: 'medium', testId: 'dialog.scheduleInterpreter', title: settled ? scheduleRowLabel(request) : SCHEDULE_LABEL },
        React.createElement("div", { className: classes.body }, settled ? (React.createElement(React.Fragment, null,
            React.createElement(Button, { accessibilityLabel: request.phase === 'cancelling'
                    ? CANCELLING_SCHEDULED_STATUS : CANCEL_SCHEDULED_BUTTON, disabled: request.phase === 'cancelling', fullWidth: true, label: request.phase === 'cancelling'
                    ? CANCELLING_SCHEDULED_STATUS : CANCEL_SCHEDULED_BUTTON, onClick: cancelScheduledRequest, type: BUTTON_TYPES.DESTRUCTIVE }),
            React.createElement(Button, { accessibilityLabel: DONE_BUTTON, fullWidth: true, label: DONE_BUTTON, onClick: close, type: BUTTON_TYPES.SECONDARY }))) : (React.createElement(React.Fragment, null,
            React.createElement("input", { "aria-label": 'Time', autoFocus: true, className: classes.time, "data-testid": 'schedule-interpreter-time', max: toLocalInputValue(new Date(now + SCHEDULE_MAX_LEAD_MS)), min: toLocalInputValue(new Date(now + SCHEDULE_MIN_LEAD_MS)), onChange: onChange, type: 'datetime-local', value: value }),
            React.createElement(Button, { accessibilityLabel: request.phase === 'scheduling'
                    ? SCHEDULING_STATUS : SCHEDULE_BUTTON, disabled: request.phase === 'scheduling' || !value, fullWidth: true, label: request.phase === 'scheduling' ? SCHEDULING_STATUS : SCHEDULE_BUTTON, onClick: schedule, type: BUTTON_TYPES.PRIMARY })))),
        React.createElement("p", { "aria-live": 'polite', className: classes.status, "data-testid": 'schedule-interpreter-status' }, request.problem || '')));
}
